/**
 * Line frames: an oriented frame for every wall surface of the room model
 * (primary edges, alcove walls, corner chamfer diagonals), and the one pair of
 * transforms between a frame and room coordinates.
 */
import { CornerName, EdgeName, FloorPlanCorner, FloorPlanRoom, Point, roomCorner } from '../schemas';

/**
 * A directed line segment with an outward perpendicular.
 * Used for arbitrary wall surfaces: primary edges, alcove walls, corner chamfers.
 * `perp` = CW rotation of `dir` = outward direction (away from room/alcove interior).
 */
export interface LineFrame {
  start: Point;
  end: Point;
  dir: Point; // unit vector from start → end
  perp: Point; // unit vector outward: {dir.y, -dir.x}
  length: number;
}

/**
 * A primary edge's frame: its line, walked clockwise round the room with
 * `perp` pointing out of it, whether it runs horizontally, and the room
 * corners at its two ends.
 */
export interface EdgeFrame extends LineFrame {
  horizontal: boolean;
  startCorner: CornerName;
  endCorner: CornerName;
}

function frameOf(start: Point, dir: Point, perp: Point, length: number, horizontal: boolean, startCorner: CornerName, endCorner: CornerName): EdgeFrame {
  return { start, end: { x: start.x + dir.x * length, y: start.y + dir.y * length }, dir, perp, length, horizontal, startCorner, endCorner };
}

export function edgeFrame(edge: EdgeName, w: number, h: number): EdgeFrame {
  switch (edge) {
    case 'backEdge':
      return frameOf({ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 0, y: -1 }, w, true, 'backLeftCorner', 'backRightCorner');
    case 'rightEdge':
      return frameOf({ x: w, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 0 }, h, false, 'backRightCorner', 'frontRightCorner');
    case 'frontEdge':
      return frameOf({ x: w, y: h }, { x: -1, y: 0 }, { x: 0, y: 1 }, w, true, 'frontRightCorner', 'frontLeftCorner');
    case 'leftEdge':
      return frameOf({ x: 0, y: h }, { x: 0, y: -1 }, { x: -1, y: 0 }, h, false, 'frontLeftCorner', 'backLeftCorner');
  }
}

export function alongLeg(c: FloorPlanCorner, horizontal: boolean): number {
  return horizontal ? c.dx : c.dy;
}
export function perpLeg(c: FloorPlanCorner, horizontal: boolean): number {
  return horizontal ? c.dy : c.dx;
}

export function makeLineFrame(start: Point, end: Point): LineFrame {
  const dx = end.x - start.x, dy = end.y - start.y;
  const length = Math.hypot(dx, dy) || 1e-9;
  const dir = { x: dx / length, y: dy / length };
  return { start, end, dir, perp: { x: dir.y, y: -dir.x }, length };
}

/** The room point at `along` the frame from its start and `out` along its `perp`. */
export function worldOnLine(lf: LineFrame, along: number, out: number): Point {
  return {
    x: lf.start.x + lf.dir.x * along + lf.perp.x * out,
    y: lf.start.y + lf.dir.y * along + lf.perp.y * out,
  };
}

/** @deprecated `worldOnLine` under its older name, kept until the editors call `worldOnLine`. */
export const toWorld = worldOnLine;

/** A room point's coordinates in the frame: how far `along` it from its start, and how far `out` along its `perp`. */
export function lineCoords(lf: LineFrame, point: Point): { along: number; out: number } {
  const dx = point.x - lf.start.x, dy = point.y - lf.start.y;
  return { along: dx * lf.dir.x + dy * lf.dir.y, out: dx * lf.perp.x + dy * lf.perp.y };
}

/**
 * LineFrame for a room corner's chamfer diagonal.
 * Returns null if the chamfer is degenerate (dx or dy is near-zero).
 */
export function roomCornerLineFrame(shape: FloorPlanRoom, corner: CornerName): LineFrame | null {
  const c = roomCorner(shape, corner);
  if (c.dx < 1e-6 || c.dy < 1e-6) return null;
  const { width: w, length: h } = shape;
  switch (corner) {
    case 'backLeftCorner':
      return makeLineFrame({ x: 0, y: c.dy }, { x: c.dx, y: 0 });
    case 'backRightCorner':
      return makeLineFrame({ x: w - c.dx, y: 0 }, { x: w, y: c.dy });
    case 'frontRightCorner':
      return makeLineFrame({ x: w, y: h - c.dy }, { x: w - c.dx, y: h });
    case 'frontLeftCorner':
      return makeLineFrame({ x: c.dx, y: h }, { x: 0, y: h - c.dy });
  }
}
